// The Codex: one book behind the book button. Its first page is the Laws,
// in the makers' riddles (src/config/verses.ts). Then the player's own
// pages: the Seals of laws witnessed, every Glyph seen with notes, the
// Order, and the Fragments the makers left. Reading it is free; it never
// costs the gold ring.

import { FRAGMENT_IDS, FRAGMENTS } from "../config/fragments.ts"
import { ICONS } from "../config/icons.ts"
import { LAW_IDS, LAWS, type LawId } from "../config/laws.ts"
import { MANUAL_TITLE, PAGES, type Page, type Plate } from "../config/verses.ts"
import { WORDS } from "../config/words.ts"
import { html, joinHtml, unsafeRawHtml, type Html } from "../boundary/html.ts"
import { restFaults } from "../domain/engine.ts"
import { orderCount } from "../domain/order.ts"
import { keysOf, setNote, type NoteKey, type Progress } from "../domain/progress.ts"
import { glyphKeyOf, ringsOf, scaleOf, type GlyphKey } from "../domain/rings.ts"
import type { Clock } from "../effects/clock.ts"
import type { Sound } from "../effects/sound.ts"
import { closest, isHidden, parts, replay, setHtml } from "./dom.ts"
import { indexRow, realmOf, sigil } from "./glyph.ts"
import { createOrder } from "./order.ts"

export const CODEX_TABS = ["laws", "seals", "glyphs", "order", "fragments"] as const
export type CodexTab = (typeof CODEX_TABS)[number]

export type CodexDeps = {
  readonly sound: Sound
  readonly clock: Clock
  readonly progress: () => Progress
  readonly change: (next: Progress) => void
}

export type CodexView = {
  readonly open: () => void
  readonly close: () => void
  readonly toggle: () => void
  readonly isOpen: () => boolean
  readonly show: (tab: CodexTab) => void
  readonly news: () => void // something new to read: the book buttons glow
}

const icon = (svg: string): Html => unsafeRawHtml(`<svg viewBox="0 0 24 24" aria-hidden="true">${svg}</svg>`)

export const miniCard = (k: GlyphKey, faults: readonly number[] = [], cls = ""): Html => {
  const t = ringsOf(k)
  const marks = faults.map((r) => `[data-ring="${r}"]`).join(",")
  return html`<div class="mini card${cls === "" ? "" : ` ${cls}`}" data-realm="${realmOf(t)}"${unsafeRawHtml(marks === "" ? "" : ` data-faults='${marks}'`)}>${indexRow(t)}${sigil(t)}</div>`
}

const ON = html`<span class="onto">on</span>`
const TO = html`<span class="arrow">→</span>`

// ---------- the Laws: the manual ----------

const plateHtml = (p: Plate): Html => {
  switch (p.kind) {
    case "legend":
      return html`<div class="law-row legend">${p.cards.map((k, i) => html`<figure class="plate-card">${miniCard(k)}<figcaption>${p.captions[i] ?? ""}</figcaption></figure>`)}</div>`
    case "rest":
      return html`<div class="law-row">${miniCard(p.upper)}${ON}${miniCard(p.lower)}</div>`
    case "refused":
      return html`<div class="law-row refused">${miniCard(p.upper, restFaults(ringsOf(p.upper), ringsOf(p.lower)))}${ON}${miniCard(p.lower, restFaults(ringsOf(p.upper), ringsOf(p.lower)))}</div>`
    case "fold":
      return html`<div class="law-row">${p.three.map((k) => miniCard(k))}${TO}${miniCard(p.made)}</div>`
    case "mirror":
      return html`<div class="law-row">${miniCard(p.truth)}<span class="plate-mark">${icon(ICONS.mirror.svg)}</span>${miniCard(p.shown)}</div>`
    case "burden": {
      const altars = Array.from({ length: p.altars }, () => html`<span class="plate-altar"></span>`)
      const cols = Array.from({ length: p.columns }, () => html`<span class="plate-bed"></span>`)
      const carried = Array.from({ length: p.carry }, () => html`<span class="plate-carried"></span>`)
      return html`<div class="law-row burden">${altars}<span class="plate-op">+1</span>${cols.length > 0 ? html`<span class="plate-op">×2</span>` : html``}${cols}${TO}<span class="plate-stack">${carried}</span></div>`
    }
    case "marks":
      return html`<div class="law-row marks">${p.icons.map((id) => html`<span class="plate-mark">${icon(ICONS[id].svg)}</span>`)}</div>`
  }
}

const isLaw = (id: string): LawId | undefined => LAW_IDS.find((l) => l === id)

const pageHtml = (page: Page): Html => {
  const law = isLaw(page.id)
  const seal = law === undefined ? html`` : html`<span class="page-seal" data-realm="${realmOf(ringsOf(LAWS[law].seal))}">${sigil(ringsOf(LAWS[law].seal))}</span>`
  const verse = joinHtml(page.verse.map((line) => html`<span class="line">${line}</span>`))
  return html`<section class="law page" id="page-${page.id}" data-page="${page.id}">
      <h3>${seal}${page.title}</h3>
      <p class="riddle">${page.riddle}</p>
      <p class="verse">${verse}</p>
      ${page.plates.map(plateHtml)}
    </section>`
}

const lawsHtml = (): Html => html`<div class="laws-page manual"><p class="manual-title">${MANUAL_TITLE}</p>${PAGES.map(pageHtml)}</div>`

// ---------- the Seals ----------

const momentHtml = (law: LawId, keys: readonly GlyphKey[]): Html =>
  joinHtml(
    keys.slice(0, 4).map((k, i) => {
      const sep = law.startsWith("fold") && i === 3 ? TO : law === "rest" && i === 1 ? html`<span class="onto">↑</span>` : html``
      return html`${sep}${miniCard(k)}`
    }),
  )

const sealHtml = (progress: Progress) => (law: LawId): Html => {
  const w = progress.laws[law]
  if (w === undefined) {
    return html`<div class="seal sealed" title="${WORDS.aria.notWitnessed}"><div class="seal-glyph">${sigil([0, 0, 0])}</div></div>`
  }
  const seal = ringsOf(LAWS[law].seal)
  return html`<div class="seal open">
      <div class="seal-glyph" data-realm="${realmOf(seal)}">${sigil(seal)}</div>
      <div class="seal-moment">${momentHtml(law, w.example)}</div>
      <div class="seal-count">${w.n}×</div>
      <p class="seal-words"><b>${LAWS[law].name}.</b></p>
    </div>`
}

const sealsHtml = (progress: Progress): Html =>
  html`<p class="panel-note">${WORDS.codex.sealsNote}</p><div class="seals">${LAW_IDS.map(sealHtml(progress))}</div>`

// ---------- the Glyphs ----------

const glyphsHtml = (progress: Progress, noting: GlyphKey | undefined): Html => {
  const keys = keysOf(progress.seen)
  const cell = (k: GlyphKey): Html => {
    const t = ringsOf(k)
    const noted = progress.notes[`glyph:${k}`] === undefined ? "" : " noted"
    return html`<button type="button" class="glyph-cell${noted}${noting === k ? " on" : ""}" data-glyph="${k}" data-realm="${realmOf(t)}" aria-label="${WORDS.aria.glyph}">${sigil(t)}${indexRow(t)}</button>`
  }
  const singles = keys.filter((k) => scaleOf(ringsOf(k)) === 1)
  const folded = keys.filter((k) => scaleOf(ringsOf(k)) > 1)
  const noteBox =
    noting === undefined
      ? html`<p class="panel-note">${WORDS.codex.glyphsNote}</p>`
      : html`<div class="glyph-note">${miniCard(noting, [], "big")}
          <textarea data-note="glyph:${noting}" rows="4" placeholder="${WORDS.codex.glyphNotes}" aria-label="${WORDS.codex.glyphNotes}">${progress.notes[`glyph:${noting}`] ?? ""}</textarea></div>`
  return html`${noteBox}
      <h4 class="codex-sub">${WORDS.codex.single}</h4><div class="glyph-grid">${singles.length > 0 ? singles.map(cell) : html`<p class="panel-note">${WORDS.codex.noneYet}</p>`}</div>
      ${folded.length > 0 ? html`<h4 class="codex-sub">${WORDS.codex.folded}</h4><div class="glyph-grid">${folded.map(cell)}</div>` : html``}`
}

// ---------- the Fragments ----------

const fragmentsHtml = (progress: Progress): Html => {
  const frag = (id: (typeof FRAGMENT_IDS)[number]): Html => {
    const t = ringsOf(FRAGMENTS[id].seal)
    return progress.fragments[id] === undefined
      ? html`<div class="frag sealed" aria-label="${WORDS.aria.notFound}"><span></span><span></span><span></span></div>`
      : html`<blockquote class="frag"><div class="frag-seal" data-realm="${realmOf(t)}">${sigil(t)}</div><p>${FRAGMENTS[id].text}</p></blockquote>`
  }
  return html`<p class="panel-note">${WORDS.codex.fragmentsNote}</p><div class="frags">${FRAGMENT_IDS.map(frag)}</div>`
}

// ---------- the book ----------

const isTab = (s: string | undefined): CodexTab | undefined => CODEX_TABS.find((t) => t === s)
const isNoteKey = (s: string | undefined): NoteKey | undefined => {
  const [kind, rest = ""] = (s ?? "").split(/:(.*)/s)
  const glyph = glyphKeyOf(rest)
  return kind === "glyph" && glyph !== undefined ? `glyph:${glyph}` : undefined
}

export const createCodex = (panel: HTMLElement, deps: CodexDeps): CodexView => {
  let tab: CodexTab = "laws"
  let noting: GlyphKey | undefined
  const order = createOrder(panel, { ...deps, rerender: () => render() })

  const tabButton = (t: CodexTab, label: string): Html =>
    html`<button type="button" data-tab="${t}" class="${tab === t ? "on" : ""}">${label}</button>`

  function render(): void {
    const progress = deps.progress()
    const scroll = panel.scrollTop
    const witnessed = LAW_IDS.filter((l) => progress.laws[l] !== undefined).length
    const count = orderCount(progress)
    const found = FRAGMENT_IDS.filter((id) => progress.fragments[id] !== undefined).length
    const body =
      tab === "laws"
        ? lawsHtml()
        : tab === "seals"
          ? sealsHtml(progress)
          : tab === "glyphs"
            ? glyphsHtml(progress, noting)
            : tab === "order"
              ? order.html()
              : fragmentsHtml(progress)
    setHtml(
      panel,
      html`<div class="panel-head">
        <h2>${WORDS.codex.title}</h2>
        <button class="close" data-close type="button" aria-label="${WORDS.codex.close}">×</button>
      </div>
      <nav class="codex-tabs">
        ${tabButton("laws", WORDS.codex.tabs.laws)}
        ${tabButton("seals", `${WORDS.codex.tabs.seals} · ${witnessed} of ${LAW_IDS.length}`)}
        ${tabButton("glyphs", `${WORDS.codex.tabs.glyphs} · ${keysOf(progress.seen).length}`)}
        ${tabButton("order", `${WORDS.codex.tabs.order} · ${count.done} of ${count.total}`)}
        ${tabButton("fragments", `${WORDS.codex.tabs.fragments} · ${found} of ${FRAGMENT_IDS.length}`)}
      </nav>
      <div class="codex-body">${body}</div>`,
    )
    panel.scrollTop = scroll
    for (const card of parts(panel, "[data-faults]")) for (const g of parts(card, card.dataset["faults"] ?? "x")) g.classList.add("fault-still")
    order.drawn()
  }

  panel.addEventListener("click", (e) => {
    if (closest(e, "[data-close]") !== undefined) return close()
    const t = isTab(closest(e, "[data-tab]")?.dataset["tab"])
    if (t !== undefined) {
      tab = t
      panel.scrollTop = 0
      render()
      return
    }
    const g = glyphKeyOf(closest(e, "[data-glyph]")?.dataset["glyph"] ?? "")
    if (g === undefined) return
    noting = g
    render()
    panel.querySelector("textarea")?.focus()
  })

  panel.addEventListener("input", (e) => {
    const t = e.target
    if (!(t instanceof HTMLTextAreaElement)) return
    const key = isNoteKey(t.dataset["note"])
    if (key !== undefined) deps.change(setNote(deps.progress(), key, t.value))
  })

  const open = (): void => {
    panel.hidden = false
    render()
    requestAnimationFrame(() => panel.classList.add("open"))
    deps.sound.chime(-6)
  }

  function close(): void {
    panel.classList.remove("open")
    panel.hidden = true
  }

  return {
    open,
    close,
    toggle: () => (isHidden(panel) ? open() : close()),
    isOpen: () => !isHidden(panel),
    show: (t) => {
      tab = t
      open()
    },
    news: () => {
      for (const b of parts(document, '[data-act="book"]')) replay(b, "news")
    },
  }
}
